import {
  bookingCellValue,
  filterColumn,
  parseListViewDateTime
} from './listViewPresentation';
import type {
  ActiveFilter,
  ListViewBooking,
  ListViewColumn,
  ListViewDiff,
  ListViewSnapshot,
  ListViewSnapshotEntry
} from '../listview/types';

/* Diffing a fetch of the Views widget against the previous fetch (the
 * "baseline snapshot"), to mark bookings that were deleted in the backend and
 * fields whose values changed. The snapshot stores the values the view
 * displays (per visible column key) and the fields its active filters read;
 * "changed" is decided on the visible columns alone, so it means exactly what
 * the user can see change, and the whole snapshot stays far below the 100kb
 * body cap of PUT /viewsnapshot for a realistic day's bookings.
 *
 * The filtered fields are there for the deleted bookings: those are rebuilt
 * from their snapshot values and then filtered like the live ones, so a
 * filter on a field that is not a column would hide every deleted booking
 * unless the snapshot carried that field too.
 *
 * The markers last exactly one refresh cycle: each successful fetch is diffed
 * against the previous snapshot, and then becomes the new baseline itself.
 * A baseline is only comparable when it is of the same day as the current
 * fetch and covers its visible columns — otherwise a booking missing from the
 * fetch would just be one of another day, not a deletion, and a column
 * missing from the baseline would read as a change on every booking. */

export const listViewSnapshotSchemaVersion = 1;

/* Slightly below the server's 100kb bodyParser cap, leaving headroom for
   encoding overhead */
const SNAPSHOT_SIZE_LIMIT = 90_000;

type SnapshotValue = ListViewSnapshotEntry['values'][string];

/* The columns a snapshot stores: the visible columns, followed by one column
   per field an active filter reads that is not among them. A filter without
   selected values counts too: values can be picked before the next refresh,
   and the field costs a few bytes per booking. */
export function snapshotColumns(
  columns: ListViewColumn[],
  activeFilters: ActiveFilter[]
): ListViewColumn[] {
  const keys = new Set(columns.map((column) => column.key));
  const filterColumns: ListViewColumn[] = [];
  activeFilters.forEach((filter) => {
    const column = filterColumn(filter);
    if (keys.has(column.key)) return;
    keys.add(column.key);
    filterColumns.push(column);
  });
  return [...columns, ...filterColumns];
}

function entryFromBooking(
  booking: ListViewBooking,
  columns: ListViewColumn[]
): ListViewSnapshotEntry {
  const values: Record<string, SnapshotValue> = {};
  columns.forEach((column) => {
    values[column.key] = bookingCellValue(booking, column);
  });
  return {
    id: booking.id,
    effectiveStart: booking.effectiveStart,
    effectiveEnd: booking.effectiveEnd,
    values,
    children: booking.children.map((child) => entryFromBooking(child, columns))
  };
}

export function snapshotFromBookings(
  bookings: ListViewBooking[],
  columns: ListViewColumn[],
  date: string,
  fetchedAt: string
): ListViewSnapshot {
  return {
    schemaVersion: listViewSnapshotSchemaVersion,
    date,
    columnKeys: columns.map((column) => column.key),
    fetchedAt,
    entries: bookings.map((booking) => entryFromBooking(booking, columns))
  };
}

/* Whether the snapshot would exceed the body cap of PUT /viewsnapshot; the
   caller then persists truncateSnapshot(snapshot) instead. Cross-session
   markers degrade for such an oversized day (no baseline after a reload), but
   in-session refreshes still diff against the in-memory snapshot. */
export function serializedSnapshotIsTooLarge(snapshot: ListViewSnapshot): boolean {
  return JSON.stringify(snapshot).length > SNAPSHOT_SIZE_LIMIT;
}

export function truncateSnapshot(snapshot: ListViewSnapshot): ListViewSnapshot {
  return { ...snapshot, entries: [], truncated: true };
}

/* The form a snapshot is persisted in: itself, or a truncated stub when it
   would not fit the body cap */
export function snapshotForStorage(snapshot: ListViewSnapshot): ListViewSnapshot {
  return serializedSnapshotIsTooLarge(snapshot) ? truncateSnapshot(snapshot) : snapshot;
}

/* Whether the snapshot can be diffed against for the given day and visible
   columns. The snapshot may hold more columns than asked for (the fields of
   filters since removed, a column since hidden): values are read by key, so
   extra ones are simply not looked at. It may not hold fewer: a visible
   column it lacks would read as a change on every booking. This is what lets
   a filter be added or removed without losing a refresh cycle of markers. */
export function snapshotIsValidBaseline(
  snapshot: ListViewSnapshot | null,
  date: string,
  columns: ListViewColumn[]
): snapshot is ListViewSnapshot {
  if (!snapshot || snapshot.schemaVersion !== listViewSnapshotSchemaVersion) return false;
  if (snapshot.truncated) return false;
  if (snapshot.date !== date) return false;
  return (
    Array.isArray(snapshot.columnKeys) &&
    columns.every((column) => snapshot.columnKeys.includes(column.key))
  );
}

/* Column values are scalars or Arrays (multi-value custom data fields) */
function valuesAreEqual(valueA: SnapshotValue, valueB: SnapshotValue): boolean {
  if (Array.isArray(valueA) || Array.isArray(valueB)) {
    if (!Array.isArray(valueA) || !Array.isArray(valueB)) return false;
    return (
      valueA.length === valueB.length &&
      valueA.every((item, index) => item === valueB[index])
    );
  }
  return valueA === valueB;
}

const CUSTOM_DATA_KEY_PREFIX = 'customdata:';

/* Reconstruct a renderable booking DTO from a snapshot entry, inverting
   bookingCellValue's column addressing: 'start'/'end' columns read the
   effective times, 'customdata:<keyword>' columns the customData bag, any
   other key a DTO field. Every value the entry holds is restored, the
   filtered fields included, so the booking can be filtered like a live one.
   Fields the snapshot never captured get neutral defaults. */
export function bookingFromSnapshotEntry(entry: ListViewSnapshotEntry): ListViewBooking {
  const booking: ListViewBooking = {
    id: entry.id,
    number: null,
    resourceID: null,
    resourceName: '',
    resourceType: null,
    resourceRoles: [],
    resourceGroup: null,
    resourceDescription: null,
    role: null,
    start: entry.effectiveStart,
    end: entry.effectiveEnd,
    effectiveStart: entry.effectiveStart,
    effectiveEnd: entry.effectiveEnd,
    comment: null,
    hasStarted: false,
    eventID: null,
    eventName: '',
    locationName: null,
    eventStatus: null,
    eventProfile: null,
    eventDirectGroup: null,
    eventTopGroup: null,
    unitPrice: null,
    customData: {},
    children: entry.children.map((child) => bookingFromSnapshotEntry(child))
  };
  Object.entries(entry.values).forEach(([key, value]) => {
    if (value === undefined) return;
    if (key.startsWith(CUSTOM_DATA_KEY_PREFIX)) {
      const keyword = key.substring(CUSTOM_DATA_KEY_PREFIX.length);
      booking.customData[keyword] = value as string | string[] | null;
      return;
    }
    if (key === 'start' || key === 'end') return;
    /* Assigning snapshot values back onto their DTO fields cannot be typed
       field-by-field; the values came from these fields via bookingCellValue */
    (booking as unknown as Record<string, SnapshotValue>)[key] = value;
  });
  return booking;
}

function flattenEntries(
  entries: ListViewSnapshotEntry[],
  index: Map<string, ListViewSnapshotEntry>
): Map<string, ListViewSnapshotEntry> {
  entries.forEach((entry) => {
    index.set(entry.id, entry);
    flattenEntries(entry.children, index);
  });
  return index;
}

function flattenBookings(
  bookings: ListViewBooking[],
  index: Map<string, ListViewBooking>
): Map<string, ListViewBooking> {
  bookings.forEach((booking) => {
    index.set(booking.id, booking);
    flattenBookings(booking.children, index);
  });
  return index;
}

/* Diff the fetched bookings against a valid baseline snapshot. Booking ids
   are globally unique (set children have their own ids), so both sides are
   compared through flat id indexes. Values are compared on the given columns
   only — the visible ones, so a filtered field the user cannot see never
   marks a row as changed. Deleted children of a surviving parent are appended
   to that parent's children, at any nesting depth (the returned bookings are
   copies where that happens); deleted top-level bookings are reconstructed
   into deletedBookings for the caller to merge into the rendered list. */
export function diffBookingsAgainstSnapshot(
  bookings: ListViewBooking[],
  snapshot: ListViewSnapshot,
  columns: ListViewColumn[]
): { diff: ListViewDiff; bookings: ListViewBooking[] } {
  const baselineByID = flattenEntries(snapshot.entries, new Map());
  const statusByID: ListViewDiff['statusByID'] = {};

  const markNewAndChanged = (booking: ListViewBooking) => {
    const baseline = baselineByID.get(booking.id);
    if (!baseline) statusByID[booking.id] = { status: 'new', changedColumnKeys: [] };
    else {
      const changedColumnKeys = columns
        .map((column) => column.key)
        .filter(
          (key) =>
            !valuesAreEqual(
              bookingCellValue(booking, { key }),
              baseline.values[key] === undefined ? null : baseline.values[key]
            )
        );
      if (changedColumnKeys.length > 0)
        statusByID[booking.id] = { status: 'changed', changedColumnKeys };
    }
    booking.children.forEach(markNewAndChanged);
  };
  bookings.forEach(markNewAndChanged);

  const fetchedByID = flattenBookings(bookings, new Map());
  const deletedBookings: ListViewBooking[] = [];
  const deletedChildrenByParentID = new Map<string, ListViewBooking[]>();

  const collectDeleted = (entry: ListViewSnapshotEntry, parentID: string | null) => {
    const isDeleted = !fetchedByID.has(entry.id);
    if (isDeleted) {
      statusByID[entry.id] = { status: 'deleted', changedColumnKeys: [] };
      const reconstructed = bookingFromSnapshotEntry(entry);
      /* A deleted entry whose whole subtree is gone is reconstructed with its
         children included (also marked deleted above via recursion below) */
      if (parentID !== null && fetchedByID.has(parentID)) {
        const siblings = deletedChildrenByParentID.get(parentID) || [];
        siblings.push(reconstructed);
        deletedChildrenByParentID.set(parentID, siblings);
      } else if (parentID === null) deletedBookings.push(reconstructed);
    }
    entry.children.forEach((child) => collectDeleted(child, entry.id));
  };
  snapshot.entries.forEach((entry) => collectDeleted(entry, null));

  /* Reattach each deleted member to the surviving set it belonged to, at
     whatever depth that set sits: a set can be a member of another set, so
     the tree is walked rather than only its top level. Copies, so the fetched
     bookings the next snapshot is taken from stay untouched. */
  const withDeletedChildren = (booking: ListViewBooking): ListViewBooking => ({
    ...booking,
    children: [
      ...booking.children.map(withDeletedChildren),
      ...(deletedChildrenByParentID.get(booking.id) || [])
    ]
  });
  const bookingsWithDeletedChildren =
    deletedChildrenByParentID.size === 0 ? bookings : bookings.map(withDeletedChildren);

  return {
    diff: { statusByID, deletedBookings },
    bookings: bookingsWithDeletedChildren
  };
}

/* One refresh cycle, as a pure function: diff the fetched bookings against
   the baseline (when it is comparable at all), and produce the snapshot that
   becomes the next baseline. The diff compares the visible columns; the
   snapshot stores those and the fields the active filters read (see
   snapshotColumns). It is taken from the raw fetched bookings, before any
   deleted children are appended for rendering, so deleted rows never leak
   into the next comparison.

   serializedDate, columns and activeFilters must be the ones the request was
   made with, not whatever the view shows by the time the response lands. */
export function diffAgainstBaseline({
  fetchedBookings,
  baseline,
  columns,
  activeFilters,
  serializedDate,
  takenAt
}: {
  fetchedBookings: ListViewBooking[];
  baseline: ListViewSnapshot | null;
  /* The visible columns */
  columns: ListViewColumn[];
  activeFilters: ActiveFilter[];
  serializedDate: string;
  takenAt: string;
}): {
  diff: ListViewDiff | null;
  bookings: ListViewBooking[];
  nextSnapshot: ListViewSnapshot;
} {
  const result = snapshotIsValidBaseline(baseline, serializedDate, columns)
    ? diffBookingsAgainstSnapshot(fetchedBookings, baseline, columns)
    : { diff: null, bookings: fetchedBookings };
  return {
    ...result,
    nextSnapshot: snapshotFromBookings(
      fetchedBookings,
      snapshotColumns(columns, activeFilters),
      serializedDate,
      takenAt
    )
  };
}

/* Merge reconstructed deleted bookings into the fetched list, keeping the
   server's chronological sort (by effectiveStart, bookings without one last)
   so grouping and pagination stay coherent */
export function mergeDeletedBookings(
  bookings: ListViewBooking[],
  deletedBookings: ListViewBooking[]
): ListViewBooking[] {
  if (deletedBookings.length === 0) return bookings;
  const sortValue = (booking: ListViewBooking): number => {
    const dateObject = parseListViewDateTime(booking.effectiveStart);
    return dateObject ? dateObject.valueOf() : Number.MAX_SAFE_INTEGER;
  };
  return [...bookings, ...deletedBookings].sort(
    (bookingA, bookingB) => sortValue(bookingA) - sortValue(bookingB)
  );
}
